import { useEffect, useRef, useState } from 'react'
import { Spinner } from '../components/icons'

/**
 * De webcam op desktop.
 *
 * `capture` op een `<input type="file">` opent op een telefoon de camera-app,
 * maar op desktop negeert de browser dat en krijg je gewoon de bestandskiezer.
 * "Webcam gebruiken" deed dus precies wat het niet moest doen. Hier staat de
 * echte camera: een videostroom, een sluiterknop, en een frame dat als JPEG
 * verdergaat in dezelfde uploadflow als een gekozen bestand.
 *
 * Alleen voor foto's. Video opnemen vraagt een MediaRecorder, een
 * opnameteller, een stopknop en een keuze uit codecs die per browser verschilt
 * — een eigen ronde waard. Wie een video wil kiest hem uit zijn bestanden.
 */
export function WebcamSheet({
  open,
  onClose,
  onCapture,
}: {
  open: boolean
  onClose: () => void
  onCapture: (file: File) => void
}) {
  const video = useRef<HTMLVideoElement>(null)
  const stream = useRef<MediaStream | null>(null)

  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }

    let cancelled = false

    async function start() {
      setError(null)
      setReady(false)

      try {
        const opened = await navigator.mediaDevices.getUserMedia({
          // De achtercamera als er een keuze is; op een laptop is er er maar één.
          video: { facingMode: 'environment', width: { ideal: 1920 } },
          audio: false,
        })

        if (cancelled) {
          opened.getTracks().forEach((track) => track.stop())

          return
        }

        stream.current = opened

        if (video.current) {
          video.current.srcObject = opened
          await video.current.play()
        }

        setReady(true)
      } catch (cause) {
        setError(
          cause instanceof DOMException && cause.name === 'NotAllowedError'
            ? 'We mogen niet bij je camera. Zet dat aan in je browser en probeer opnieuw.'
            : 'We vinden geen camera op dit toestel.',
        )
      }
    }

    void start()

    return () => {
      cancelled = true

      // Het lampje naast de camera hoort uit te gaan zodra dit paneel dicht is.
      stream.current?.getTracks().forEach((track) => track.stop())
      stream.current = null
    }
  }, [open])

  function shoot() {
    const source = video.current

    if (!source) {
      return
    }

    const canvas = document.createElement('canvas')

    canvas.width = source.videoWidth
    canvas.height = source.videoHeight

    canvas.getContext('2d')?.drawImage(source, 0, 0)

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('Die foto lukte niet. Probeer nog eens.')

          return
        }

        // Dezelfde vorm als een gekozen bestand, zodat de rest van de
        // uploadflow geen verschil ziet.
        onCapture(new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' }))
      },
      'image/jpeg',
      0.92,
    )
  }

  if (!open) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim-viewer)] p-6">
      <div className="bg-viewer rounded-sheet w-full max-w-[720px] overflow-hidden">
        <div className="relative aspect-[4/3] bg-black">
          <video
            ref={video}
            playsInline
            muted
            className="h-full w-full object-cover"
            aria-label="Wat je camera ziet"
          />

          {!ready && !error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner className="size-6 text-white/70" />
            </div>
          )}

          {error && (
            <p
              role="alert"
              className="absolute inset-0 flex items-center justify-center px-10 text-center text-[15px] leading-[1.5] text-white/85"
            >
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="text-viewer-ink-muted hover:text-viewer-ink px-2 py-2 text-[15px] transition-colors"
          >
            Annuleren
          </button>

          <button
            type="button"
            onClick={shoot}
            disabled={!ready}
            aria-label="Foto maken"
            className="bg-accent text-accent-ink h-13 rounded-full px-8 text-base font-semibold transition hover:brightness-95 disabled:opacity-40"
          >
            Foto maken
          </button>

          <span aria-hidden="true" className="w-20" />
        </div>
      </div>
    </div>
  )
}
